import React, { useEffect, useRef } from 'react';
import './FileDownload.scss';
import { useLazyDownloadFileQuery } from './rtkApi';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import { showToast } from '@klpkonsern/ui';

interface DownloadFileProps {
    readonly path?: string;
    readonly name?: string;
    readonly preview?: boolean;
    readonly showNotifications?: boolean;
    readonly stopTableClickPropagation?: boolean;
    fileContentField?: string;
    queryParams?: object;
    children?: JSX.Element;
}

export const FileDownload = ({
    path,
    name,
    fileContentField,
    queryParams,
    preview = false,
    children,
    stopTableClickPropagation = false,
}: DownloadFileProps) => {
    const { t } = useTranslation();
    const [downloadFile, { isLoading, data }] = useLazyDownloadFileQuery();
    const shouldOpenPreview = useRef(false);

    const handleDownload = async (event: React.MouseEvent<HTMLDivElement>) => {
        if (stopTableClickPropagation) {
            event.stopPropagation();
        }

        if (!path || !name) return;

        if (preview) {
            shouldOpenPreview.current = true;
        }

        showToast({ body: t('file.downloading'), type: 'info' });

        try {
            const result = await downloadFile({
                filePath: path,
                fileName: name,
                fileContentField,
                queryParams,
            }).unwrap();

            if (result) {
                showToast({
                    body: t('file.downloadingFinished'),
                    type: 'success',
                });
            }
        } catch (error) {
            showToast({
                body: t('file.downloadFailed'),
                type: 'error',
            });
        }
    };

    useEffect(() => {
        if (data && shouldOpenPreview.current) {
            const url = URL.createObjectURL(data.blob);
            window.open(url, 'preview');
            shouldOpenPreview.current = false;
        } else if (data && !preview) {
            saveAs(data.blob, data.fileName);
        }
    }, [data, preview]);

    if (isLoading) {
        return <div>{children}</div>;
    }

    return <div onClick={handleDownload}>{children}</div>;
};
